import React from 'react';
import { X, Sparkles, Bug, Zap, Plus } from 'lucide-react';

interface ChangelogEntry {
  version: string;
  date: string;
  changes: {
    type: 'feature' | 'fix' | 'improvement' | 'new';
    description: string;
  }[];
}

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CHANGELOG_DATA: ChangelogEntry[] = [
  {
    version: '3.3.0',
    date: '24 Maggio 2026',
    changes: [
      {
        type: 'new',
        description: 'Workflows automatici: crea sequenze condizionali con triggers, ramificazioni e azioni come task, email, tag, attesa con un editor visuale completo'
      },
      {
        type: 'new',
        description: 'QR Code dinamici: genera codici tracciabili con URL modificabili nel tempo e statistiche di scansione (incluso nel piano Quantum)'
      },
      {
        type: 'new',
        description: 'Video screen + webcam: registra direttamente dal CRM (tipo Loom) e condividi via link con clienti o team (piano Matrix)'
      },
      {
        type: 'new',
        description: 'App mobile iOS e Android nativa: notifiche push, chat WhatsApp integrata, lead e attività on the go'
      },
      {
        type: 'new',
        description: 'Sezione "Scopri estensioni" nella sidebar: vedi cosa è disponibile come add-on o nei piani superiori senza navigare nei prezzi'
      },
      {
        type: 'new',
        description: 'Integrazione Voxloud VoIP: registra automaticamente le chiamate, trascrizione e analisi AI direttamente nel CRM'
      },
      {
        type: 'new',
        description: 'Meta Lead Ads webhook: i lead generati su Facebook/Instagram entrano automaticamente nel CRM con assegnazione automatica'
      },
      {
        type: 'feature',
        description: 'Permessi dei ruoli ridisegnati: configurazione feature per feature, livelli "Solo lettura / Modifica / Pieno controllo" e opzioni avanzate spiegate in italiano'
      },
      {
        type: 'feature',
        description: 'Template ruoli pronti all\'uso: applica con un click "Solo lettura", "Modifica base", "Responsabile team" o "Pieno controllo"'
      },
      {
        type: 'feature',
        description: 'IVA automatica sui pagamenti Stripe: aggiunta automatica del 22% IVA su tutti i nuovi abbonamenti, gestione reverse charge per clienti UE B2B'
      },
      {
        type: 'feature',
        description: 'Riassunto AI delle conversazioni: genera note di riepilogo automatiche dalle chat WhatsApp e dalle call registrate'
      },
      {
        type: 'feature',
        description: 'Workflow trigger su tag, deal stage, importo deal, calendario, scadenza task e molti altri eventi'
      },
      {
        type: 'feature',
        description: 'Form di acquisizione lead con campi personalizzati, validazione e webhook su submit'
      },
      {
        type: 'improvement',
        description: 'Caricamento più rapido del CRM grazie a chunk dedicati per pagina e cache più aggressiva'
      },
      {
        type: 'improvement',
        description: 'Performance dashboard: filtri salvati, refresh meno frequenti, indicatori più reattivi'
      },
      {
        type: 'improvement',
        description: 'Notifiche push più affidabili con conferma di consegna e gestione dispositivi multipli'
      },
      {
        type: 'improvement',
        description: 'Disconnessione automatica delle sessioni scadute con redirect al login senza errori'
      },
      {
        type: 'improvement',
        description: 'Velocità ricerca clienti, lead e deal migliorata grazie a indici ottimizzati'
      },
      {
        type: 'fix',
        description: 'Risolti bug di visualizzazione del calendario con eventi Google sincronizzati'
      },
      {
        type: 'fix',
        description: 'Corretta gestione fasce utenti per piani con prezzi a scaglioni nel cambio piano'
      },
      {
        type: 'fix',
        description: 'Risolto problema di caricamento delle attività in ritardo con molti record'
      },
      {
        type: 'fix',
        description: 'Allegati nelle task ora correttamente visibili anche dal portale cliente'
      }
    ]
  },
  {
    version: '3.2.0',
    date: '23 Febbraio 2026',
    changes: [
      {
        type: 'new',
        description: 'Sistema Sondaggi completo tipo Typeform con builder visuale, logica condizionale e analytics risposte'
      },
      {
        type: 'new',
        description: 'Sistema Booking pubblico tipo Calendly con URL dedicati, calcolo slot automatico e reminder'
      },
      {
        type: 'new',
        description: 'Integrazione Vexa AI per registrazione meeting, trascrizione automatica e summary AI'
      },
      {
        type: 'new',
        description: 'Generazione automatica status update progetto tramite AI con cron orario'
      },
      {
        type: 'feature',
        description: 'Invio proposte commerciali via email e WhatsApp con PDF allegato direttamente dal deal'
      },
      {
        type: 'feature',
        description: 'Disponibilità personalizzabile per singolo event type nel sistema booking'
      },
      {
        type: 'improvement',
        description: 'Notifiche automatiche per task in scadenza con controllo orario via cron'
      },
      {
        type: 'improvement',
        description: 'Digest giornaliero sullo stato dei progetti con email riepilogativa al team'
      }
    ]
  },
  {
    version: '3.1.0',
    date: '5 Febbraio 2026',
    changes: [
      {
        type: 'new',
        description: 'Portale Cliente con dashboard dedicata, vista progetti, task e commenti in tempo reale'
      },
      {
        type: 'new',
        description: 'Portale Collaboratore per gestione task assegnati a collaboratori esterni'
      },
      {
        type: 'new',
        description: 'Team Chat interna con messaggistica real-time e organizzazione in canali'
      },
      {
        type: 'new',
        description: 'Brief progetto con supporto documenti allegati e condivisione col team'
      },
      {
        type: 'feature',
        description: 'Tag su task di progetto per organizzazione e filtraggio avanzato'
      },
      {
        type: 'feature',
        description: 'Milestone di progetto con stati configurabili e date target'
      },
      {
        type: 'improvement',
        description: 'Controllo automatico scadenze progetto con notifiche al team e al responsabile'
      }
    ]
  },
  {
    version: '3.0.0',
    date: '10 Gennaio 2026',
    changes: [
      {
        type: 'new',
        description: 'Sistema Ticketing completo con tipi configurabili, priorità e time tracking'
      },
      {
        type: 'new',
        description: 'Contatti multipli associabili alle offerte per gestione stakeholder avanzata'
      },
      {
        type: 'new',
        description: 'Trascrizione audio automatica per note vocali e registrazioni'
      },
      {
        type: 'feature',
        description: 'Suggerimento prossima attività tramite AI basato su contesto lead e deal'
      },
      {
        type: 'feature',
        description: 'Suggerimento automatico creazione offerta dalle conversazioni WhatsApp'
      },
      {
        type: 'feature',
        description: 'AI Data Insights per generazione automatica insight dai dati del CRM'
      },
      {
        type: 'improvement',
        description: 'Log email notifiche per monitoraggio delivery e bounce nel pannello super admin'
      },
      {
        type: 'fix',
        description: 'Corretti cron project digest e notification digest per esecuzione affidabile'
      }
    ]
  },
  {
    version: '2.9.0',
    date: '14 Novembre 2025',
    changes: [
      {
        type: 'new',
        description: 'Sistema completo di gestione campagne email e WhatsApp con template personalizzabili'
      },
      {
        type: 'new',
        description: 'Gestione progetti con kanban board, task assignment e tracking progresso'
      },
      {
        type: 'new',
        description: 'Supporto pipelines multiple per gestione flussi di vendita personalizzati'
      },
      {
        type: 'new',
        description: 'Integrazione Google Meet per video call direttamente dalle attività dei lead'
      },
      {
        type: 'feature',
        description: 'Aggiornamenti real-time nei lead per collaborazione sincronizzata del team'
      },
      {
        type: 'feature',
        description: 'Modal di aggiunta veloce lead per inserimento rapido senza cambiare pagina'
      },
      {
        type: 'feature',
        description: 'Sistema trigger automatici con invio WhatsApp basato su eventi e condizioni'
      },
      {
        type: 'feature',
        description: 'Trigger cron schedulati e sistema completo di automazioni workflow'
      },
      {
        type: 'feature',
        description: 'Import avanzato migliorato per clienti, lead e offerte con validazione potenziata'
      },
      {
        type: 'feature',
        description: 'Possibilità di caricare e gestire file allegati direttamente nelle attività'
      }
    ]
  },
  {
    version: '2.5.0',
    date: '3 Ottobre 2025',
    changes: [
      {
        type: 'new',
        description: 'Sistema WhatsApp Worker AI con riconoscimento automatico utenti e comandi naturali (testo, audio, immagini)'
      },
      {
        type: 'new',
        description: 'Sistema Import Avanzato con mapping intelligente, preview dati, validazione e rollback completo'
      },
      {
        type: 'new',
        description: 'Collegamento Partner-Referrer per gestione commissioni integrata tra partner e segnalatori'
      },
      {
        type: 'improvement',
        description: 'Ottimizzazione drastica performance calcolo commissioni (da ~300 a 8-10 query, 97% più veloce)'
      },
      {
        type: 'improvement',
        description: 'Ristrutturazione completa menu navigazione con nuova architettura e configurazione centralizzata'
      },
      {
        type: 'improvement',
        description: 'Rinnovato pannello notifiche con gestione avanzata e migliore UX'
      },
      {
        type: 'improvement',
        description: 'Revisione header e interfaccia sezioni Lead, Clienti, Partner e Attività'
      },
      {
        type: 'improvement',
        description: 'Migrazione completa icone menu a FontAwesome per consistenza UI'
      },
      {
        type: 'fix',
        description: 'Risolti problemi di isolamento organization nel sistema import utenti'
      },
      {
        type: 'fix',
        description: 'Correzioni multiple UI, performance e gestione sessioni WhatsApp'
      }
    ]
  },
  {
    version: '2.4.2',
    date: '12 Agosto 2025',
    changes: [
      {
        type: 'new',
        description: 'Aggiunta sincronizzazione WooCommerce bidirezionale nella sezione magazzino'
      },
      {
        type: 'feature',
        description: 'Implementato servizio WooCommerce con autenticazione API e sync completo'
      },
      {
        type: 'new',
        description: 'Interfaccia dedicata per configurazione e gestione sync eCommerce'
      },
      {
        type: 'improvement',
        description: 'Matching intelligente prodotti/categorie tramite SKU e nome'
      },
      {
        type: 'fix',
        description: 'Risolto bug focus input nel form prodotti magazzino'
      }
    ]
  },
  {
    version: '2.4.1',
    date: '29 Luglio 2025',
    changes: [
      {
        type: 'new',
        description: 'Aggiunto sistema di template variabili per descrizioni servizi'
      },
      {
        type: 'new',
        description: 'Implementato pulsanti help collassabili in tutte le sezioni del form servizi'
      },
      {
        type: 'improvement',
        description: 'Migliorata compatibilità template variabili con sistema PDF esistente'
      },
      {
        type: 'fix',
        description: 'Risolto errore MyTasksPage con proprietà undefined'
      },
      {
        type: 'improvement',
        description: 'Rimossa sezione variabili servizio obsoleta, sostituita con sistema template'
      }
    ]
  },
  {
    version: '2.4.0',
    date: '15 Luglio 2025',
    changes: [
      {
        type: 'feature',
        description: 'Sistema di gestione commissioni avanzate per servizi'
      },
      {
        type: 'feature',
        description: 'Task di rinnovo automatiche configurabili'
      },
      {
        type: 'improvement',
        description: 'Ottimizzazione performance caricamento menu'
      },
      {
        type: 'fix',
        description: 'Risolti problemi di sincronizzazione WhatsApp'
      }
    ]
  },
  {
    version: '2.3.8',
    date: '8 Luglio 2025',
    changes: [
      {
        type: 'feature',
        description: 'Integrazione fatturazione automatica con coda invoice'
      },
      {
        type: 'improvement',
        description: 'Migliorata UX form creazione servizi'
      },
      {
        type: 'fix',
        description: 'Corretti problemi visualizzazione mobile'
      }
    ]
  }
];

const getChangeIcon = (type: string) => {
  switch (type) {
    case 'feature':
      return <Sparkles className="h-4 w-4 text-purple-600" />;
    case 'fix':
      return <Bug className="h-4 w-4 text-red-600" />;
    case 'improvement':
      return <Zap className="h-4 w-4 text-blue-600" />;
    case 'new':
      return <Plus className="h-4 w-4 text-green-600" />;
    default:
      return <Sparkles className="h-4 w-4 text-gray-600" />;
  }
};

const getChangeLabel = (type: string) => {
  switch (type) {
    case 'feature':
      return 'Funzionalità';
    case 'fix':
      return 'Correzione';
    case 'improvement':
      return 'Miglioramento';
    case 'new':
      return 'Novità';
    default:
      return 'Modifica';
  }
};

export function ChangelogModal({ isOpen, onClose }: ChangelogModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sparkles className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Novità e Aggiornamenti</h2>
              <p className="text-sm text-gray-600">Le ultime funzionalità del CRM</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="p-6 space-y-6">
            {CHANGELOG_DATA.map((entry, index) => (
              <div key={entry.version} className="relative">
                {/* Version Header */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-[#0f6cbd] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    v{entry.version}
                  </div>
                  <span className="text-sm text-gray-500">{entry.date}</span>
                </div>

                {/* Changes */}
                <div className="space-y-3 ml-6">
                  {entry.changes.map((change, changeIndex) => (
                    <div key={changeIndex} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getChangeIcon(change.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {getChangeLabel(change.type)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 mt-1">{change.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                {index < CHANGELOG_DATA.length - 1 && (
                  <div className="mt-6 border-b border-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Versione corrente: <span className="font-semibold">v{CHANGELOG_DATA[0]?.version}</span>
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#0f6cbd] text-white rounded-lg text-sm font-medium hover:bg-[#0a5ba8] transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}